import { useEffect, useRef, useState } from 'react';

import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';

import { queries } from '@api';
import { ApiResponseError } from '@api/apiUtils/ApiResponseError';
import { alert } from '@components/ui/Alert/Alert.utilities';
import { CREATE_NEW_ANALYSIS } from '@navigation/routes.paths';
import { useCustomMutation } from '@utils/hooks/useCustomMutation';
import { useSubmitFormMutation } from '@utils/hooks/useSubmitFormMutation';

import { TERMINAL_REVIEW_STATES } from './api.aiReviews.constants';
import {
  getAiReviewOptions,
  getAiReviewsStatsOptions,
  getLastReviewOptions,
  getMyReviewsOptions,
  getPublicAiReviewOptions,
} from './api.aiReviews.options';
import {
  cancelAiReview,
  createAiReview,
  deleteAiReview,
  restartAiReview,
  submitFeedback,
  togglePublicSharing,
  updateSharedEmails,
} from './api.aiReviews.requests';

import type {
  AIReviewFetchResponse,
  AIReviewStatsResponse,
  FastReviewPayload,
  MyReviewsResponse,
  PublicAIReviewFetchResponse,
  TogglePublicSharingPayload,
  SubmitFeedbackPayload,
  UpdateSharedEmailsPayload,
} from './api.aiReviews.types';

// === Query Hooks ===

/**
 * Hook for retrieving single AI Review by ID
 * @param id - Review ID
 * @param enabled - Optional flag to enable/disable the query (defaults to true if id is provided)
 */
export const useRetrieveAiReview = (
  id: string,
  enabled = true,
): UseQueryResult<AIReviewFetchResponse> => {
  const queryClient = useQueryClient();
  const result = useQuery({
    ...getAiReviewOptions(id),
    enabled: !!id && enabled,
  });

  const previousStateReference = useRef<string | undefined>(undefined);
  const reviewState = result.data?.state;

  useEffect(() => {
    if (!reviewState) return;

    const wasInProgress =
      previousStateReference.current &&
      !TERMINAL_REVIEW_STATES.has(previousStateReference.current);
    const isNowTerminal = TERMINAL_REVIEW_STATES.has(reviewState);

    if (wasInProgress && isNowTerminal) {
      void queryClient.invalidateQueries({
        queryKey: queries.aiReviews.myReviews.queryKey,
      });
    }

    previousStateReference.current = reviewState;
  }, [reviewState, queryClient]);

  return result;
};

/**
 * Hook for retrieving public/shared AI Review
 */
export const useGetPublicAiReview = (
  id: string,
): UseQueryResult<PublicAIReviewFetchResponse> => {
  return useQuery({
    ...getPublicAiReviewOptions(id),
    enabled: !!id,
  });
};

/**
 * Hook for retrieving current user's reviews
 */
export const useGetMyReviews = (): UseQueryResult<MyReviewsResponse> => {
  return useQuery(getMyReviewsOptions);
};

/**
 * Hook for retrieving user's last review
 * @param enabled - Optional flag to enable/disable the query (defaults to true)
 */
export const useGetLastReview = (
  enabled = true,
): UseQueryResult<AIReviewFetchResponse> => {
  return useQuery({
    ...getLastReviewOptions,
    enabled,
  });
};

/**
 * Hook for retrieving AI Reviews statistics
 * Returns completed and in-progress counts
 */
export const useGetAiReviewsStats =
  (): UseQueryResult<AIReviewStatsResponse> => {
    return useQuery(getAiReviewsStatsOptions);
  };

// === Mutation Hooks ===

/**
 * Hook for cancelling AI Review and its related tasks
 */
export const useCancelAiReview = (id: string) => {
  const queryClient = useQueryClient();

  return useCustomMutation(
    queries.aiReviews.cancel(id).queryKey,
    () => cancelAiReview(id),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.retrieve(id).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.myReviews.queryKey,
        });
      },
    },
  );
};

/**
 * Hook for restarting failed AI Review from failure point.
 *
 * Корреляция с BACK-745: бэк отдаёт 400 (нет restartable AITask) или 404
 * (review уже soft-deleted в другой вкладке/Admin). В обоих случаях retry
 * больше не возможен — показываем toast, чистим кэш и уводим пользователя
 * на создание нового анализа.
 */
export const useRestartAiReview = (id: string) => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useCustomMutation(
    queries.aiReviews.restart(id).queryKey,
    () => restartAiReview(id),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.retrieve(id).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.myReviews.queryKey,
        });
      },
      onError: (error) => {
        const isTerminalRestartError =
          error instanceof ApiResponseError &&
          (error.code === 400 || error.code === 404);

        if (!isTerminalRestartError) return;

        alert.error('Этот анализ больше нельзя перезапустить, создайте новый');

        queryClient.removeQueries({
          queryKey: queries.aiReviews.retrieve(id).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.myReviews.queryKey,
        });

        void router.navigate({ to: CREATE_NEW_ANALYSIS });
      },
    },
  );
};

/**
 * Hook for deleting AI Review
 */
export const useDeleteAiReview = (id: string) => {
  const queryClient = useQueryClient();

  return useCustomMutation(
    queries.aiReviews.delete(id).queryKey,
    () => deleteAiReview(id),
    {
      onSuccess: () => {
        // Remove from cache
        queryClient.removeQueries({
          queryKey: queries.aiReviews.retrieve(id).queryKey,
        });

        // Invalidate my reviews list
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.myReviews.queryKey,
        });
      },
    },
  );
};

/**
 * Hook for toggling public sharing
 */
export const useTogglePublicSharing = (id: string) => {
  const queryClient = useQueryClient();

  return useCustomMutation<TogglePublicSharingPayload, AIReviewFetchResponse>(
    queries.aiReviews.togglePublicSharing(id).queryKey,
    payload => togglePublicSharing(id, payload),
    {
      onSuccess: updatedReview => {
        // Update cache for specific review
        queryClient.setQueryData(
          queries.aiReviews.retrieve(id).queryKey,
          updatedReview,
        );
      },
    },
  );
};

/**
 * Hook for updating shared emails
 */
export const useUpdateSharedEmails = (id: string) => {
  const queryClient = useQueryClient();

  return useCustomMutation<UpdateSharedEmailsPayload, AIReviewFetchResponse>(
    queries.aiReviews.updateSharedEmails(id).queryKey,
    payload => updateSharedEmails(id, payload),
    {
      onSuccess: updatedReview => {
        // Update cache for specific review
        queryClient.setQueryData(
          queries.aiReviews.retrieve(id).queryKey,
          updatedReview,
        );
      },
    },
  );
};

/**
 * Hook for submitting feedback for AI Review
 * Updates lastReview cache on success
 */
export const useSubmitFeedback = (id: string) => {
  const queryClient = useQueryClient();

  return useCustomMutation<SubmitFeedbackPayload, AIReviewFetchResponse>(
    queries.aiReviews.submitFeedback(id).queryKey,
    (payload) => submitFeedback(id, payload),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.lastReview.queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.retrieve(id).queryKey,
        });
      },
    },
  );
};

/**
 * Hook for creating AI Review with file upload
 * Uses useSubmitFormMutation for TanStack Form integration
 * API returns 201 Created with AIReviewFetchResponse
 */
export const useCreateAiReview = () => {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useSubmitFormMutation<FastReviewPayload, AIReviewFetchResponse>(
    queries.aiReviews.create.queryKey,
    (payload: FastReviewPayload) =>
      createAiReview(payload, (event) => {
        if (event.total) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      }),
    {
      onSuccess: () => {
        setUploadProgress(0);
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.myReviews.queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: queries.aiReviews.lastReview.queryKey,
        });
      },
      onError: () => {
        setUploadProgress(0);
      },
    },
  );

  return { ...mutation, uploadProgress };
};
